// ============================================================================
// Safe Care - Carte d'export animée
// Module : features/assistance_sociale/components
//
// Au clic sur "Exporter", le fichier généré (Blob) descend au centre de
// l'écran au-dessus de tout (au lieu de partir directement en téléchargement).
// Deux actions :
//   - "Plus tard"   : télécharge simplement le fichier ;
//   - "Visualiser"  : affiche un aperçu intégré (iframe pour le PDF, tableaux
//                     HTML pour Excel/Word) ; à la fermeture de l'aperçu le
//                     fichier se télécharge automatiquement.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { Button, Space, Typography } from 'antd';
import {
  ClockCircleOutlined, DownloadOutlined, EyeOutlined,
  FileExcelOutlined, FilePdfOutlined, FileWordOutlined,
} from '@ant-design/icons';
import { saveAs } from 'file-saver';
import { ExportFile } from '../services/exportFile';

const iconFor = (ext: string, fontSize: number) => {
  const style = { fontSize };
  if (ext === 'pdf') return <FilePdfOutlined style={{ ...style, color: '#ef4444' }} />;
  if (ext === 'xlsx') return <FileExcelOutlined style={{ ...style, color: '#10b981' }} />;
  return <FileWordOutlined style={{ ...style, color: '#3b82f6' }} />;
};

const taille = (b: Blob) => (b.size >= 1048576 ? `${(b.size / 1048576).toFixed(1)} Mo` : `${(b.size / 1024).toFixed(1)} Ko`);

const ExportFileModal = ({ file, onDone }: { file: ExportFile | null; onDone: () => void }) => {
  const [preview, setPreview] = useState(false);
  useEffect(() => setPreview(false), [file]);

  // URL blob pour l'aperçu iframe du PDF (révocable)
  const objectUrl = useMemo(() => (file?.ext === 'pdf' ? URL.createObjectURL(file.blob) : null), [file]);
  useEffect(() => () => { if (objectUrl) URL.revokeObjectURL(objectUrl); }, [objectUrl]);

  if (!file) return null;

  const download = () => {
    saveAs(file.blob, file.name);
    onDone();
  };

  return (
    <div className="exportFileOverlay">
      {!preview ? (
        /* Carte animée : descend depuis le haut et se centre au-dessus de tout */
        <div className="exportFileCard" key={file.name}>
          <div style={{ fontSize: 64, lineHeight: 1 }}>{iconFor(file.ext, 64)}</div>
          <Typography.Title level={4} style={{ marginTop: 12, marginBottom: 4 }}>Export prêt</Typography.Title>
          <div style={{ fontWeight: 600, wordBreak: 'break-all' }}>{file.name}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{taille(file.blob)} — {file.ext.toUpperCase()}</Typography.Text>
          <div style={{ marginTop: 18 }}>
            <Space>
              <Button type="primary" icon={<EyeOutlined />} onClick={() => setPreview(true)}>
                Visualiser
              </Button>
              <Button icon={<ClockCircleOutlined />} onClick={download}>
                Plus tard
              </Button>
            </Space>
          </div>
        </div>
      ) : (
        /* Aperçu du fichier ; à la fermeture le fichier se télécharge */
        <div className="exportFilePreview">
          <div className="exportFilePreviewHeader">
            <Space>
              {iconFor(file.ext, 22)}
              <b>{file.name}</b>
            </Space>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Le fichier sera téléchargé à la fermeture
            </Typography.Text>
            <Button type="primary" icon={<DownloadOutlined />} onClick={download}>
              Fermer et télécharger
            </Button>
          </div>
          <div className="exportFilePreviewBody">
            {file.ext === 'pdf' && objectUrl ? (
              <iframe title={file.name} src={objectUrl} />
            ) : (
              <div
                className="exportHtmlPreview"
                dangerouslySetInnerHTML={{ __html: file.previewHtml ?? '<p>Aperçu non disponible pour ce format.</p>' }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportFileModal;
